import { Injectable, computed, signal } from '@angular/core';

// Evento que Chrome y Edge disparan cuando la tienda se puede instalar (no está en lib.dom)
interface EventoInstalar extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Dónde se abrió: cambia cómo se instala */
export type Plataforma = 'ios-safari' | 'ios-chrome' | 'ios-otro' | 'android' | 'escritorio';

const CLAVE_DESCARTE = 'vitrina.instalar.descartado.v2';
// "Ahora no" la oculta un día; después vuelve a invitar
const PAUSA_MS = 24 * 3600_000;

function detectar(): Plataforma {
  if (typeof navigator === 'undefined') return 'escritorio';
  const ua = navigator.userAgent;
  // iPadOS se presenta como Mac: se reconoce por la pantalla táctil
  const ios = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  if (ios) return /crios/i.test(ua) ? 'ios-chrome' : /fxios|edgios|opios/i.test(ua) ? 'ios-otro' : 'ios-safari';
  return /android/i.test(ua) ? 'android' : 'escritorio';
}

/**
 * La tienda como app (D-147). Registra el service worker (lo usan las notificaciones push) y
 * guarda la invitación de instalar del navegador para mostrarla con un botón propio. Donde no hay
 * invitación (iPhone en cualquier navegador desde iOS 16.4, o Android sin ella) se explica cómo
 * agregarla a la pantalla de inicio.
 */
@Injectable({ providedIn: 'root' })
export class AppInstalableService {
  private evento: EventoInstalar | null = null;
  private readonly puede = signal(false);
  private readonly instaladaAhora = signal(false);

  readonly plataforma = detectar();
  readonly esIos = this.plataforma.startsWith('ios');
  readonly esMovil = this.plataforma !== 'escritorio';
  readonly abiertaComoApp =
    typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

  /** Hay botón "Instalar la app": invitación del navegador o instrucciones (celular) */
  readonly disponible = computed(() => !this.abiertaComoApp && !this.instaladaAhora() && (this.puede() || this.esMovil));
  readonly instalada = computed(() => this.abiertaComoApp || this.instaladaAhora());
  /** La persona cerró la invitación que sube desde abajo hace menos de 24 h: queda el botón del menú */
  readonly descartada = signal(this.leerDescarte());

  /** Una sola vez, al arrancar la app */
  iniciar(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.evento = e as EventoInstalar;
      this.puede.set(true);
    });
    window.addEventListener('appinstalled', () => {
      this.instaladaAhora.set(true);
      this.puede.set(false);
    });
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  }

  /** Abre la ventana del navegador. Devuelve false si no hay: mostrar las instrucciones. */
  async instalar(): Promise<boolean> {
    if (!this.evento) return false;
    await this.evento.prompt();
    const { outcome } = await this.evento.userChoice;
    this.evento = null;
    this.puede.set(false);
    if (outcome === 'accepted') this.instaladaAhora.set(true);
    return true;
  }

  descartar(): void {
    this.descartada.set(true);
    try {
      localStorage.setItem(CLAVE_DESCARTE, String(Date.now()));
    } catch {
      // Sin almacenamiento: vuelve a invitar en la próxima visita
    }
  }

  private leerDescarte(): boolean {
    try {
      const cuando = Number(localStorage.getItem(CLAVE_DESCARTE));
      return Boolean(cuando) && Date.now() - cuando < PAUSA_MS;
    } catch {
      return false;
    }
  }
}
